import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";
import PasswordInput from "./PasswordInput";
import { DevCredit } from "./DeveloperCard";

const GRAD = {
  navy:   "linear-gradient(135deg, #0f1f3d 0%, #1a3a6b 100%)",
  blue:   "linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)",
  green:  "linear-gradient(135deg, #27ae60 0%, #1a7a3f 100%)",
  orange: "linear-gradient(135deg, #f39c12 0%, #e67e22 100%)",
  teal:   "linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)",
  purple: "linear-gradient(135deg, #8e44ad 0%, #6c3483 100%)",
  danger: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
};

const C = {
  navy: "#0f1f3d", blue: "#1a73e8", danger: "#e74c3c",
  success: "#27ae60", warn: "#f39c12", bg: "#f0f2f5",
  border: "#dde1e7", text: "#2c3e50", muted: "#7f8c8d",
};

// ── Reusable: blurred modal backdrop ─────────────────────────
function Modal({ title, onClose, children, width = 500 }) {
  return (
    <div style={{
      position: "fixed", inset: 0,
      background: "rgba(15,31,61,0.55)",
      backdropFilter: "blur(2px)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 14,
        padding: "28px 32px", width: "100%", maxWidth: width,
        boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <h3 style={{ margin: 0, color: C.navy, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{
            width: 30, height: 30, border: "none", borderRadius: 8,
            background: "#f0f2f6", cursor: "pointer", fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center", color: C.muted,
          }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Form helpers ──────────────────────────────────────────────
const FL = ({ label, span, children }) => (
  <div style={{ marginBottom: 16, gridColumn: span === 2 ? "1 / -1" : undefined }}>
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} style={{
    width: "100%", padding: "10px 14px",
    border: `1.5px solid ${C.border}`, borderRadius: 8,
    fontSize: 14, boxSizing: "border-box", outline: "none",
    transition: "border-color 0.2s",
  }} />
);

// ── Gradient KPI Stat Card ────────────────────────────────────
function StatCard({ label, value, gradient, icon, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: gradient, borderRadius: 12,
      padding: "22px 24px", color: "#fff",
      boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
      cursor: onClick ? "pointer" : "default",
      position: "relative", overflow: "hidden",
      transition: "transform 0.15s, box-shadow 0.15s",
    }}
      onMouseEnter={e => onClick && (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={e => onClick && (e.currentTarget.style.transform = "")}
    >
      {/* ghost icon */}
      <span style={{
        position: "absolute", right: 16, bottom: 6,
        fontSize: 52, opacity: 0.18, lineHeight: 1,
        userSelect: "none", pointerEvents: "none",
      }}>{icon}</span>
      {/* glow circle */}
      <div style={{
        position: "absolute", top: -24, right: -24,
        width: 90, height: 90, borderRadius: "50%",
        background: "rgba(255,255,255,0.12)",
        pointerEvents: "none",
      }} />
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, opacity: 0.82, textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</p>
      <p style={{ margin: "8px 0 0", fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Full report list for analytics tab
  const [reportList, setReportList]       = useState([]);
  const [reportTotal, setReportTotal]     = useState(0);
  const [reportSearch, setReportSearch]   = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [expandedReport, setExpandedReport] = useState(null); // id of expanded row
  const [reportSubTab, setReportSubTab]   = useState("all"); // "all" | "preliminary" | "final" | "analysis"

  // Bank list management
  const [bankList, setBankList]       = useState([]);
  const [newBankInput, setNewBankInput] = useState("");
  const [bankSaving, setBankSaving]   = useState(false);
  const [bankMsg, setBankMsg]         = useState("");

  // Valuator management
  const emptyValuator = () => ({ id: String(Date.now()+Math.random()), name:"", licenseNo:"", company:"", phone:"", email:"" });
  const [valuatorList, setValuatorList]   = useState([]);
  const [valuatorSaving, setValuatorSaving] = useState(false);
  const [valuatorMsg, setValuatorMsg]     = useState("");
  const [editingValuator, setEditingValuator] = useState(null); // null | valuator object

  const [modal, setModal] = useState(null);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try { setUsers(await api.listUsers()); } catch (e) { console.error(e); }
  }, []);

  const loadProfile = useCallback(async () => {
    try { setProfile(await api.getCompanyProfile()); } catch (e) { console.error(e); }
  }, []);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await api.getReportStats()); }
    catch (e) { console.error(e); }
    finally { setStatsLoading(false); }
  }, []);

  const loadReports = useCallback(async (search = "") => {
    setReportLoading(true);
    try {
      const d = await api.listAdminReports({ search, limit: 200 });
      setReportList(d.reports || []);
      setReportTotal(d.total || 0);
    } catch (e) { console.error(e); }
    finally { setReportLoading(false); }
  }, []);

  const loadBanks = useCallback(async () => {
    try { const d = await api.getCompanyBanks(); setBankList(d.banks || []); }
    catch (e) { console.error(e); }
  }, []);

  const loadValuators = useCallback(async () => {
    try { const d = await api.getCompanyValuators(); setValuatorList(d.valuators || []); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadUsers(); loadProfile(); loadBanks(); loadValuators(); }, [loadUsers, loadProfile, loadBanks, loadValuators]);
  useEffect(() => { if (tab === "reports") { loadStats(); loadReports(reportSearch); } }, [tab]); // mount-only per tab switch

  // ── Bank list helpers ─────────────────────────────────────
  const saveBanks = async (list) => {
    setBankSaving(true); setBankMsg("");
    try {
      const d = await api.updateCompanyBanks(list);
      setBankList(d.banks);
      setBankMsg("✓ Bank list saved.");
      setTimeout(() => setBankMsg(""), 3000);
    } catch (e) {
      setBankMsg("Error: " + e.message);
    } finally {
      setBankSaving(false);
    }
  };

  const addBank = () => {
    const name = newBankInput.trim();
    if (!name || bankList.includes(name)) return;
    const updated = [...bankList, name];
    setNewBankInput("");
    saveBanks(updated);
  };

  const removeBank = (b) => saveBanks(bankList.filter(x => x !== b));

  const moveBank = (i, dir) => {
    const l = [...bankList];
    const j = i + dir;
    if (j < 0 || j >= l.length) return;
    [l[i], l[j]] = [l[j], l[i]];
    saveBanks(l);
  };

  // ── Valuator helpers ──────────────────────────────────────
  const saveValuators = async (list) => {
    setValuatorSaving(true); setValuatorMsg("");
    try {
      const d = await api.updateCompanyValuators(list);
      setValuatorList(d.valuators);
      setValuatorMsg("✓ Valuator list saved.");
      setTimeout(() => setValuatorMsg(""), 3000);
    } catch (e) {
      setValuatorMsg("Error: " + e.message);
    } finally {
      setValuatorSaving(false);
    }
  };

  const saveEditingValuator = () => {
    if (!editingValuator || !editingValuator.name.trim()) return;
    const exists = valuatorList.find(v => v.id === editingValuator.id);
    const updated = exists
      ? valuatorList.map(v => v.id === editingValuator.id ? editingValuator : v)
      : [...valuatorList, editingValuator];
    setEditingValuator(null);
    saveValuators(updated);
  };

  const removeValuator = (id) => saveValuators(valuatorList.filter(v => v.id !== id));

  const openModal = (name, row = null) => {
    setError(""); setTarget(row);
    setForm(row ? { ...row } : {});
    setModal(name);
  };
  const closeModal = () => { setModal(null); setTarget(null); setForm({}); setError(""); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));

  const saveUser = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      if (modal === "addUser") await api.createUser(form);
      else await api.updateUser(target.id, form);
      await loadUsers(); closeModal();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const deleteUser = async () => {
    setSaving(true); setError("");
    try { await api.deleteUser(target.id); await loadUsers(); closeModal(); }
    catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault(); setProfileSaving(true); setProfileMsg("");
    try {
      await api.updateCompanyProfile(profile);
      setProfileMsg("success");
    } catch (err) { setProfileMsg("error:" + err.message); }
    finally { setProfileSaving(false); }
  };

  const active = users.filter(u => !u.must_change_password).length;
  const tempPwd = users.filter(u => u.must_change_password).length;

  const TABS = [
    ["users",     "👥", "User Management"],
    ["valuators", "👷", "Valuators"],
    ["banks",     "🏦", "Bank List"],
    ["reports",   "📊", "Report Analytics"],
    ["profile",   "🏢", "Company Profile"],
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Segoe UI', sans-serif" }}>

      {/* ── Sticky Gradient Header ── */}
      <div style={{
        background: GRAD.navy, color: "#fff",
        padding: "0 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        minHeight: 64, position: "sticky", top: 0, zIndex: 100,
        boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
      }}>
        {/* left: logo box + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "rgba(255,255,255,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, flexShrink: 0,
          }}>🏢</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
              {profile?.company_name || user.companyCode} — Admin Panel
            </div>
            <div style={{ fontSize: 10, opacity: 0.45, letterSpacing: "0.3px" }}>
              <DevCredit style={{ color: "rgba(255,255,255,0.45)" }} />
            </div>
          </div>
        </div>

        {/* right: admin badge + user pill + sign out */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            background: GRAD.orange, color: "#fff",
            borderRadius: 6, padding: "3px 10px",
            fontSize: 11, fontWeight: 800, letterSpacing: "0.5px",
          }}>ADMIN</span>

          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "rgba(255,255,255,0.1)", borderRadius: 20,
            padding: "5px 12px 5px 6px",
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: "50%",
              background: GRAD.blue,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700,
            }}>
              {user.username.charAt(0).toUpperCase()}
            </div>
            <span style={{ fontSize: 13 }}>{user.username}</span>
          </div>

          <button
            onClick={onLogout}
            style={{
              background: "rgba(231,76,60,0.85)", color: "#fff",
              border: "none", borderRadius: 8,
              padding: "7px 14px", cursor: "pointer",
              fontSize: 13, fontWeight: 600,
            }}
          >Sign Out</button>
        </div>
      </div>

      <div style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>

        {/* ── KPI Stat Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 28 }}>
          <StatCard label="Total Users"    value={users.length}                          gradient={GRAD.navy}   icon="👥" />
          <StatCard label="Active"         value={active}                                gradient={GRAD.green}  icon="✅" />
          <StatCard label="Temp Password"  value={tempPwd}                               gradient={GRAD.orange} icon="🔑" />
          <StatCard label="Total Reports"  value={stats?.totals?.total ?? "—"}           gradient={GRAD.blue}   icon="📋" onClick={() => setTab("reports")} />
          <StatCard label="Final Reports"  value={stats?.totals?.final ?? "—"}           gradient={GRAD.teal}   icon="📌" onClick={() => setTab("reports")} />
        </div>

        {/* ── Pill Tab Nav ── */}
        <div style={{
          display: "flex", gap: 6,
          background: "#fff", borderRadius: 12, padding: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          marginBottom: 24, width: "fit-content",
        }}>
          {TABS.map(([t, icon, label]) => {
            const active = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: "9px 22px", border: "none", borderRadius: 8,
                cursor: "pointer", fontSize: 14, fontWeight: 600,
                background: active ? GRAD.blue : "transparent",
                color: active ? "#fff" : C.muted,
                boxShadow: active ? "0 2px 8px rgba(26,115,232,0.35)" : "none",
                transition: "all 0.2s",
                display: "flex", alignItems: "center", gap: 7,
              }}>
                <span>{icon}</span> {label}
              </button>
            );
          })}
        </div>

        {/* ══════════════════════════════════════════
            TAB: USER MANAGEMENT
        ══════════════════════════════════════════ */}
        {tab === "users" && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            {/* Card header */}
            <div style={{
              padding: "18px 24px",
              borderBottom: `2px solid ${C.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>Users</h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{users.length} member{users.length !== 1 ? "s" : ""} in your company</p>
              </div>
              <button
                onClick={() => openModal("addUser")}
                style={{
                  background: GRAD.blue, color: "#fff",
                  border: "none", borderRadius: 8,
                  padding: "9px 18px", cursor: "pointer",
                  fontSize: 13, fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(26,115,232,0.3)",
                }}
              >+ Add User</button>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["Username", "Email", "Status", "Created", "Actions"].map(h => (
                    <th key={h} style={{
                      padding: "11px 18px", textAlign: "left",
                      fontSize: 11, fontWeight: 700, color: C.muted,
                      textTransform: "uppercase", letterSpacing: "0.5px",
                      background: "#fafbfd", borderBottom: `2px solid ${C.border}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 14 }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>👤</div>
                      No users yet. Click "+ Add User" to create one.
                    </td>
                  </tr>
                ) : users.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd" }}>
                    <td style={{ padding: "13px 18px", fontSize: 14, color: C.text }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: GRAD.blue,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>{u.username.charAt(0).toUpperCase()}</div>
                        <strong>{u.username}</strong>
                      </div>
                    </td>
                    <td style={{ padding: "13px 18px", fontSize: 14, color: C.text }}>
                      {u.email || <span style={{ color: C.muted }}>—</span>}
                    </td>
                    <td style={{ padding: "13px 18px" }}>
                      {u.must_change_password ? (
                        <span style={{ background: "#fff8e1", color: "#e67e22", border: "1px solid #f39c1244", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>TEMP PASSWORD</span>
                      ) : (
                        <span style={{ background: "#e8f5e9", color: "#27ae60", border: "1px solid #27ae6044", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>ACTIVE</span>
                      )}
                    </td>
                    <td style={{ padding: "13px 18px", fontSize: 13, color: C.muted }}>{u.created_at?.slice(0, 10)}</td>
                    <td style={{ padding: "13px 18px" }}>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={() => openModal("editUser", u)}
                          style={{ background: "#f0f4ff", color: C.blue, border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                        >Edit</button>
                        <button
                          onClick={() => openModal("deleteUser", u)}
                          style={{ background: "#fdecea", color: C.danger, border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                        >Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: VALUATORS
        ══════════════════════════════════════════ */}
        {tab === "valuators" && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>👷 Company Valuators</h2>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted }}>
                  Valuators added here can be selected by users when filling a report in Section 1.
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {valuatorMsg && (
                  <span style={{ fontSize: 13, color: valuatorMsg.startsWith("Error") ? C.danger : C.success, fontWeight: 600 }}>
                    {valuatorMsg}
                  </span>
                )}
                <button
                  onClick={() => setEditingValuator(emptyValuator())}
                  style={{ padding: "9px 20px", background: C.blue, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >
                  + Add Valuator
                </button>
              </div>
            </div>

            {/* Inline edit form */}
            {editingValuator && (
              <div style={{ padding: "20px 28px", background: "#f0f4ff", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 14 }}>
                  {valuatorList.find(v => v.id === editingValuator.id) ? "✏️ Edit Valuator" : "➕ New Valuator"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px" }}>
                  {[
                    ["name",      "Valuator Name *",       "e.g. Er. Ram Sharma",          false],
                    ["licenseNo", "NEC / License No.",     'e.g. 11518 Civil "A"',         false],
                    ["company",   "Company / Firm",        "e.g. Neo-Civic Consulting",    false],
                    ["phone",     "Phone / Mobile",        "e.g. 98xxxxxxxx",              false],
                    ["email",     "Email",                 "e.g. ram@example.com",         true ],
                  ].map(([key, label, placeholder, isEmail]) => (
                    <div key={key}>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#555", marginBottom: 5, textTransform: "uppercase" }}>{label}</label>
                      <input
                        type={isEmail ? "email" : "text"}
                        value={editingValuator[key] || ""}
                        onChange={e => setEditingValuator(v => ({ ...v, [key]: e.target.value }))}
                        placeholder={placeholder}
                        style={{ width: "100%", padding: "9px 13px", border: `1.5px solid ${C.border}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button
                    onClick={saveEditingValuator}
                    disabled={!editingValuator.name.trim() || valuatorSaving}
                    style={{ padding: "8px 22px", background: C.success, color: "#fff", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: !editingValuator.name.trim() ? 0.5 : 1 }}
                  >
                    {valuatorSaving ? "Saving…" : "💾 Save"}
                  </button>
                  <button
                    onClick={() => setEditingValuator(null)}
                    style={{ padding: "8px 18px", background: "#fff", color: C.text, border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Valuator list */}
            {valuatorList.length === 0 && !editingValuator ? (
              <div style={{ padding: "40px 28px", textAlign: "center", color: C.muted, fontStyle: "italic", fontSize: 14 }}>
                No valuators added yet. Click "+ Add Valuator" to create the first one.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Name", "License No.", "Company / Firm", "Phone", "Email", "Actions"].map(h => (
                      <th key={h} style={{ padding: "10px 20px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, background: "#fafbfc" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {valuatorList.map((v, i) => (
                    <tr key={v.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      <td style={{ padding: "12px 20px", fontWeight: 600, fontSize: 14, color: C.navy }}>{v.name}</td>
                      <td style={{ padding: "12px 20px", fontSize: 13, color: C.text }}>{v.licenseNo || <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={{ padding: "12px 20px", fontSize: 13, color: C.text }}>{v.company || <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={{ padding: "12px 20px", fontSize: 13, color: C.text }}>{v.phone || <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={{ padding: "12px 20px", fontSize: 13, color: C.text }}>{v.email || <span style={{ color: C.muted }}>—</span>}</td>
                      <td style={{ padding: "8px 20px" }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => setEditingValuator({ ...v })}
                            style={{ padding: "5px 14px", background: "#fff", color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => removeValuator(v.id)}
                            disabled={valuatorSaving}
                            style={{ padding: "5px 14px", background: "#fff", color: C.danger, border: `1px solid ${C.danger}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ padding: "14px 28px", background: "#f8faff", borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, borderRadius: "0 0 14px 14px" }}>
              💡 Users will see these valuators in a dropdown in Section 1 of the valuation form.
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: BANK LIST MANAGEMENT
        ══════════════════════════════════════════ */}
        {tab === "banks" && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>🏦 Company Bank List</h2>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: C.muted }}>
                  Banks added here appear at the top of the bank dropdown for all users in your company.
                </p>
              </div>
              {bankMsg && (
                <span style={{ fontSize: 13, color: bankMsg.startsWith("Error") ? C.danger : C.success, fontWeight: 600 }}>
                  {bankMsg}
                </span>
              )}
            </div>

            {/* Add bank input */}
            <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                value={newBankInput}
                onChange={e => setNewBankInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addBank()}
                placeholder="Type bank name and press Enter or click Add…"
                style={{ flex: 1, minWidth: 220, padding: "9px 14px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: "none" }}
              />
              <button
                onClick={addBank}
                disabled={!newBankInput.trim() || bankList.includes(newBankInput.trim()) || bankSaving}
                style={{ padding: "9px 22px", background: C.blue, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: (!newBankInput.trim() || bankList.includes(newBankInput.trim())) ? 0.5 : 1 }}
              >
                {bankSaving ? "Saving…" : "+ Add Bank"}
              </button>
            </div>

            {/* Bank list */}
            {bankList.length === 0 ? (
              <div style={{ padding: "40px 28px", textAlign: "center", color: C.muted, fontStyle: "italic", fontSize: 14 }}>
                No custom banks added yet. Use the input above to add your first bank.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Bank Name", "Order", "Remove"].map(h => (
                      <th key={h} style={{ padding: "10px 20px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", borderBottom: `1px solid ${C.border}`, background: "#fafbfc" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bankList.map((b, i) => (
                    <tr key={b} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      <td style={{ padding: "11px 20px", color: C.muted, fontSize: 13, width: 40 }}>{i + 1}</td>
                      <td style={{ padding: "11px 20px", fontWeight: 500, fontSize: 14, color: C.text }}>{b}</td>
                      <td style={{ padding: "8px 20px", width: 100 }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => moveBank(i, -1)} disabled={i === 0 || bankSaving}
                            style={{ padding: "3px 8px", border: `1px solid ${C.border}`, borderRadius: 4, background: "#fff", cursor: i === 0 ? "not-allowed" : "pointer", opacity: i === 0 ? 0.4 : 1, fontSize: 12 }}>▲</button>
                          <button onClick={() => moveBank(i, 1)} disabled={i === bankList.length - 1 || bankSaving}
                            style={{ padding: "3px 8px", border: `1px solid ${C.border}`, borderRadius: 4, background: "#fff", cursor: i === bankList.length - 1 ? "not-allowed" : "pointer", opacity: i === bankList.length - 1 ? 0.4 : 1, fontSize: 12 }}>▼</button>
                        </div>
                      </td>
                      <td style={{ padding: "8px 20px", width: 80 }}>
                        <button onClick={() => removeBank(b)} disabled={bankSaving}
                          style={{ padding: "4px 12px", background: "#fff", color: C.danger, border: `1px solid ${C.danger}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          ✕ Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ padding: "14px 28px", background: "#f8faff", borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, borderRadius: "0 0 14px 14px" }}>
              💡 Changes are saved immediately. All users in your company will see the updated list in their bank dropdown.
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: REPORT ANALYTICS
        ══════════════════════════════════════════ */}
        {tab === "reports" && (
          <div>
            {/* Summary KPI cards */}
            {stats && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                <StatCard label="Total Reports"       value={stats.totals?.total ?? 0}       gradient={GRAD.navy}   icon="📋" />
                <StatCard label="Preliminary Reports" value={stats.totals?.preliminary ?? 0} gradient={GRAD.blue}   icon="📝"
                  onClick={() => setReportSubTab("preliminary")} />
                <StatCard label="Final Reports"       value={stats.totals?.final ?? 0}       gradient={GRAD.green}  icon="✅"
                  onClick={() => setReportSubTab("final")} />
              </div>
            )}

            {/* Sub-tab pills */}
            <div style={{ display: "flex", gap: 6, marginBottom: 18, background: "#fff", borderRadius: 10, padding: 5, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", width: "fit-content" }}>
              {[
                ["all",         "📋", "All Reports"],
                ["preliminary", "📝", "Preliminary"],
                ["final",       "✅", "Final"],
                ["analysis",    "📊", "Bank / Branch Analysis"],
              ].map(([key, icon, label]) => (
                <button key={key} onClick={() => setReportSubTab(key)} style={{
                  padding: "8px 18px", border: "none", borderRadius: 7, cursor: "pointer",
                  fontSize: 13, fontWeight: 600,
                  background: reportSubTab === key ? GRAD.blue : "transparent",
                  color: reportSubTab === key ? "#fff" : C.muted,
                  boxShadow: reportSubTab === key ? "0 2px 8px rgba(26,115,232,0.3)" : "none",
                  transition: "all 0.18s",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span>{icon}</span>{label}
                </button>
              ))}
            </div>

            {/* ── Bank / Branch Analysis ── */}
            {reportSubTab === "analysis" && (() => {
              const bankMap = {};
              reportList.forEach(r => {
                const bank   = r.bank   || "Unknown Bank";
                const branch = r.branch || "Unknown Branch";
                if (!bankMap[bank]) bankMap[bank] = { total: 0, preliminary: 0, final: 0, branches: {} };
                bankMap[bank].total++;
                bankMap[bank][r.report_type] = (bankMap[bank][r.report_type] || 0) + 1;
                if (!bankMap[bank].branches[branch]) bankMap[bank].branches[branch] = { total: 0, preliminary: 0, final: 0 };
                bankMap[bank].branches[branch].total++;
                bankMap[bank].branches[branch][r.report_type] = (bankMap[bank].branches[branch][r.report_type] || 0) + 1;
              });
              const banks = Object.entries(bankMap).sort((a, b) => b[1].total - a[1].total);
              const maxTotal = banks[0]?.[1].total || 1;
              return (
                <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
                  <div style={{ padding: "18px 24px", borderBottom: `2px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>📊 Bank / Branch Analysis</h2>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{banks.length} bank{banks.length !== 1 ? "s" : ""} · {reportList.length} total reports</p>
                    </div>
                    <button onClick={() => { loadStats(); loadReports(reportSearch); }}
                      style={{ padding: "8px 14px", background: "#f0f2f5", color: C.text, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>↺ Refresh</button>
                  </div>
                  {banks.length === 0 ? (
                    <div style={{ padding: 48, textAlign: "center", color: C.muted }}>No report data yet.</div>
                  ) : banks.map(([bankName, bankData]) => {
                    const branches = Object.entries(bankData.branches).sort((a, b) => b[1].total - a[1].total);
                    return (
                      <div key={bankName} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ padding: "14px 24px", background: "#f8faff", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <div style={{ fontWeight: 700, fontSize: 15, color: C.navy }}>🏦 {bankName}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{branches.length} branch{branches.length !== 1 ? "es" : ""}</div>
                          </div>
                          <div style={{ flex: 2, minWidth: 200 }}>
                            <div style={{ display: "flex", height: 18, borderRadius: 9, overflow: "hidden", background: "#e8ecf4" }}>
                              <div style={{ width: `${(bankData.preliminary / maxTotal) * 100}%`, background: "#1565c0", transition: "width 0.4s" }} title={`Preliminary: ${bankData.preliminary}`} />
                              <div style={{ width: `${(bankData.final / maxTotal) * 100}%`, background: C.success, transition: "width 0.4s" }} title={`Final: ${bankData.final}`} />
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                            <span style={{ background: "#e8f0fe", color: "#1565c0", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>P: {bankData.preliminary}</span>
                            <span style={{ background: "#e8f5e9", color: C.success, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>F: {bankData.final}</span>
                            <span style={{ background: "#f0f2f5", color: C.text, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>Total: {bankData.total}</span>
                          </div>
                        </div>
                        {branches.map(([branchName, branchData]) => (
                          <div key={branchName} style={{ padding: "10px 24px 10px 48px", display: "flex", alignItems: "center", gap: 16, borderTop: `1px solid ${C.border}`, flexWrap: "wrap" }}>
                            <div style={{ flex: 1, minWidth: 160, fontSize: 13, color: C.text }}>
                              <span style={{ color: C.muted, marginRight: 6 }}>└</span>{branchName}
                            </div>
                            <div style={{ flex: 2, minWidth: 160 }}>
                              <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "#e8ecf4" }}>
                                <div style={{ width: `${(branchData.preliminary / maxTotal) * 100}%`, background: "#1565c0" }} />
                                <div style={{ width: `${(branchData.final / maxTotal) * 100}%`, background: C.success }} />
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                              <span style={{ background: "#e8f0fe", color: "#1565c0", borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>P: {branchData.preliminary}</span>
                              <span style={{ background: "#e8f5e9", color: C.success, borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>F: {branchData.final}</span>
                              <span style={{ background: "#f0f2f5", color: C.text, borderRadius: 5, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{branchData.total}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  <div style={{ padding: "12px 24px", background: "#fafbfd", display: "flex", gap: 16, fontSize: 12, color: C.muted }}>
                    <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#1565c0", borderRadius: 2, marginRight: 5, verticalAlign: "middle" }}/>Preliminary</span>
                    <span><span style={{ display: "inline-block", width: 12, height: 12, background: C.success, borderRadius: 2, marginRight: 5, verticalAlign: "middle" }}/>Final</span>
                    <span style={{ marginLeft: "auto" }}>Bar width relative to highest-volume bank</span>
                  </div>
                </div>
              );
            })()}

            {/* ── Report list (All / Preliminary / Final) ── */}
            {reportSubTab !== "analysis" && (() => {
              const filtered = reportSubTab === "all"
                ? reportList
                : reportList.filter(r => r.report_type === reportSubTab);

              return (
                <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
                  {/* Toolbar */}
                  <div style={{ padding: "18px 24px", borderBottom: `2px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>
                        {reportSubTab === "all" ? "📋 All Reports" : reportSubTab === "preliminary" ? "📝 Preliminary Reports" : "✅ Final Reports"}
                      </h2>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{filtered.length} report{filtered.length !== 1 ? "s" : ""}</p>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.muted, fontSize: 14 }}>🔍</span>
                        <input
                          type="text"
                          placeholder="Search client, bank, branch, user…"
                          value={reportSearch}
                          onChange={e => setReportSearch(e.target.value)}
                          onKeyDown={e => e.key === "Enter" && loadReports(reportSearch)}
                          style={{ padding: "8px 12px 8px 32px", border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 13, width: 260, outline: "none" }}
                        />
                        {reportSearch && (
                          <button onClick={() => { setReportSearch(""); loadReports(""); }}
                            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14 }}>✕</button>
                        )}
                      </div>
                      <button onClick={() => loadReports(reportSearch)}
                        style={{ padding: "8px 16px", background: GRAD.blue, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Search</button>
                      <button onClick={() => { loadStats(); loadReports(reportSearch); }}
                        style={{ padding: "8px 14px", background: "#f0f2f5", color: C.text, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>↺ Refresh</button>
                    </div>
                  </div>

                  {reportLoading ? (
                    <div style={{ padding: 48, textAlign: "center", color: C.muted }}>Loading reports…</div>
                  ) : filtered.length === 0 ? (
                    <div style={{ padding: 48, textAlign: "center", color: C.muted }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>No reports found.
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {["#", "Client / Owner", "Bank", "Branch", "Type", "Visit Date", "Report Date", "Created By", "Saved", "Print History"].map(h => (
                              <th key={h} style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.4px", background: "#fafbfd", borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((r, i) => {
                            const prelPrints  = r.prints.filter(p => p.print_type === "preliminary");
                            const finalPrints = r.prints.filter(p => p.print_type === "final");
                            const isExpanded  = expandedReport === r.id;
                            return (
                              <React.Fragment key={r.id}>
                                <tr style={{ background: i % 2 === 0 ? "#fff" : "#fafbfd", cursor: r.prints.length > 0 ? "pointer" : "default" }}
                                  onClick={() => r.prints.length > 0 && setExpandedReport(isExpanded ? null : r.id)}>
                                  <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted }}>{r.id}</td>
                                  <td style={{ padding: "11px 14px", fontSize: 14, fontWeight: 500, color: C.text, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.client_name || "—"}</td>
                                  <td style={{ padding: "11px 14px", fontSize: 13, color: C.text, whiteSpace: "nowrap" }}>{r.bank || "—"}</td>
                                  <td style={{ padding: "11px 14px", fontSize: 13, color: C.text }}>{r.branch || "—"}</td>
                                  <td style={{ padding: "11px 14px" }}>
                                    <span style={{ background: r.report_type === "preliminary" ? "#e8f0fe" : "#e8f5e9", color: r.report_type === "preliminary" ? "#1565c0" : C.success, borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{r.report_type}</span>
                                  </td>
                                  <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{r.visit_date || "—"}</td>
                                  <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{r.report_date || "—"}</td>
                                  <td style={{ padding: "11px 14px", fontSize: 13, color: C.navy, fontWeight: 600 }}>
                                    {r.created_by ? (
                                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{ width: 22, height: 22, borderRadius: "50%", background: GRAD.blue, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{r.created_by.charAt(0).toUpperCase()}</span>
                                        {r.created_by}
                                      </span>
                                    ) : <span style={{ color: C.muted }}>—</span>}
                                  </td>
                                  <td style={{ padding: "11px 14px", fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>{r.updated_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                                  <td style={{ padding: "11px 14px", whiteSpace: "nowrap" }}>
                                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                      {prelPrints.length  > 0 && <span style={{ background: "#e8f0fe", color: "#1565c0", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>P×{prelPrints.length}</span>}
                                      {finalPrints.length > 0 && <span style={{ background: "#e8f5e9", color: C.success, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>F×{finalPrints.length}</span>}
                                      {r.prints.length === 0 && <span style={{ color: C.muted, fontSize: 12 }}>Not printed</span>}
                                      {r.prints.length > 0 && <span style={{ color: C.muted, fontSize: 12 }}>{isExpanded ? "▲" : "▼"}</span>}
                                    </div>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={10} style={{ padding: "0 14px 12px 40px", background: "#f0f4ff" }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, padding: "10px 0 6px", textTransform: "uppercase", letterSpacing: "0.4px" }}>🖨 Print / Download History</div>
                                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                        <thead>
                                          <tr>{["User", "Report Type", "Action", "Date & Time"].map(h => (
                                            <th key={h} style={{ padding: "5px 10px", textAlign: "left", color: C.muted, fontWeight: 700, textTransform: "uppercase", fontSize: 10, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                                          ))}</tr>
                                        </thead>
                                        <tbody>
                                          {r.prints.map((p, pi) => (
                                            <tr key={pi} style={{ background: pi % 2 === 0 ? "#fff" : "#f8faff" }}>
                                              <td style={{ padding: "6px 10px", fontWeight: 600, color: C.navy }}>
                                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                  <span style={{ width: 18, height: 18, borderRadius: "50%", background: GRAD.blue, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{p.username.charAt(0).toUpperCase()}</span>
                                                  {p.username}
                                                </span>
                                              </td>
                                              <td style={{ padding: "6px 10px" }}>
                                                <span style={{ background: p.print_type === "preliminary" ? "#e8f0fe" : "#e8f5e9", color: p.print_type === "preliminary" ? "#1565c0" : C.success, borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{p.print_type}</span>
                                              </td>
                                              <td style={{ padding: "6px 10px" }}>
                                                <span style={{ background: p.action === "download" ? "#fff8e1" : "#f3e5f5", color: p.action === "download" ? "#e67e22" : "#8e44ad", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>
                                                  {p.action === "download" ? "⬇ Download" : "🖨 Print"}
                                                </span>
                                              </td>
                                              <td style={{ padding: "6px 10px", color: C.muted }}>{p.printed_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: COMPANY PROFILE
        ══════════════════════════════════════════ */}
        {tab === "profile" && profile && (
          <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
            <div style={{
              padding: "18px 24px 16px",
              borderBottom: `2px solid ${C.border}`,
              display: "flex", alignItems: "center", gap: 14,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: GRAD.navy,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20,
              }}>🏢</div>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy }}>Company Profile</h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>Update your company's contact information and address</p>
              </div>
            </div>

            <form onSubmit={saveProfile} style={{ padding: "28px 28px 24px" }}>
              {profileMsg === "success" && (
                <div style={{ background: "#e8f5e9", color: C.success, border: "1px solid #a5d6a7", borderRadius: 8, padding: "11px 16px", fontSize: 14, marginBottom: 20, fontWeight: 600 }}>
                  ✓ Company profile saved successfully.
                </div>
              )}
              {profileMsg.startsWith("error:") && (
                <div style={{ background: "#fdecea", color: C.danger, border: "1px solid #f5c6cb", borderRadius: 8, padding: "11px 16px", fontSize: 14, marginBottom: 20 }}>
                  {profileMsg.slice(6)}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FL label="Company Name" span={2}><Input value={profile.company_name || ""} onChange={setP("company_name")} placeholder="Your company's legal name" /></FL>
                <FL label="Address Line 1"><Input value={profile.address1 || ""} onChange={setP("address1")} placeholder="Street address" /></FL>
                <FL label="Address Line 2"><Input value={profile.address2 || ""} onChange={setP("address2")} placeholder="Suite, floor, etc." /></FL>
                <FL label="City"><Input value={profile.city || ""} onChange={setP("city")} /></FL>
                <FL label="State / Province"><Input value={profile.state || ""} onChange={setP("state")} /></FL>
                <FL label="ZIP / Postal Code"><Input value={profile.zip || ""} onChange={setP("zip")} /></FL>
                <FL label="Country"><Input value={profile.country || ""} onChange={setP("country")} /></FL>
                <FL label="Contact Email"><Input type="email" value={profile.contact_email || ""} onChange={setP("contact_email")} placeholder="contact@company.com" /></FL>
                <FL label="Contact Phone"><Input type="tel" value={profile.contact_phone || ""} onChange={setP("contact_phone")} placeholder="+977-..." /></FL>
              </div>

              <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  disabled={profileSaving}
                  style={{
                    background: profileSaving ? "#ccc" : GRAD.blue,
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "10px 24px", cursor: profileSaving ? "default" : "pointer",
                    fontSize: 14, fontWeight: 700,
                    boxShadow: profileSaving ? "none" : "0 2px 8px rgba(26,115,232,0.3)",
                  }}
                >{profileSaving ? "Saving…" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════ */}
      {(modal === "addUser" || modal === "editUser") && (
        <Modal title={modal === "addUser" ? "Add New User" : `Edit User — ${target?.username}`} onClose={closeModal}>
          {error && (
            <div style={{ background: "#fdecea", color: C.danger, border: "1px solid #f5c6cb", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}
          <form onSubmit={saveUser}>
            <FL label="Username *"><Input value={form.username || ""} onChange={set("username")} required placeholder="e.g. john.doe" /></FL>
            <FL label="Email"><Input type="email" value={form.email || ""} onChange={set("email")} placeholder="optional" /></FL>
            <FL label={modal === "editUser" ? "New Password (leave blank to keep)" : "Temporary Password *"}>
              <PasswordInput value={form.password || ""} onChange={set("password")} required={modal === "addUser"} placeholder="Min 6 characters" />
            </FL>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" onClick={closeModal} style={{ background: "#f0f2f5", color: C.text, border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ background: saving ? "#ccc" : GRAD.blue, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", cursor: saving ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
                {saving ? "Saving…" : modal === "addUser" ? "Create User" : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "deleteUser" && (
        <Modal title="Delete User" onClose={closeModal} width={420}>
          {error && (
            <div style={{ background: "#fdecea", color: C.danger, border: "1px solid #f5c6cb", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16 }}>{error}</div>
          )}
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
            <p style={{ margin: 0, fontSize: 15, color: C.text }}>
              Delete user <strong style={{ color: C.navy }}>{target?.username}</strong>?
            </p>
            <p style={{ margin: "8px 0 0", fontSize: 13, color: C.muted }}>This action cannot be undone.</p>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={closeModal} style={{ background: "#f0f2f5", color: C.text, border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Cancel</button>
            <button onClick={deleteUser} disabled={saving} style={{ background: saving ? "#ccc" : GRAD.danger, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", cursor: saving ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
              {saving ? "Deleting…" : "Delete User"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
